import React from "react";
import { getOptimizedVersion } from "../services/processOptimizationService";
import {
  readRedesignProvenance,
  buildSaveSummary,
  carriesAsStructure,
  defaultAdoptKeys,
  readAppliedHeuristics,
  readDeclinedHeuristics,
  readStructuralHeuristics,
  readSelectionWarnings,
} from "../utils/redesignProvenance";

/**
 * Drives the Save tab's engine selector: which engines compose the version being
 * saved, and the save payload that selection produces.
 *
 * The agent is the base, not an option — it is what produced the redesign in the
 * first place, so its checkbox is permanently checked and disabled. RL and BPR are
 * only selectable once a run for THIS process exists, because composing them
 * requires the run's reference (rl_job_id / bpr_result_ref). Without a reference
 * the backend re-runs the engine, and for RL — a stochastic search — a re-run can
 * land on a different redesign than the one the analyst approved.
 *
 * Selecting nothing beyond the agent omits `engines` entirely, which leaves the
 * endpoint behaving exactly as it always has: no prefetch, no provenance, and the
 * save falls back to the fetch it has always done at save time.
 */
export default function useSaveEngineSelection({ selectedId, rl, bpr }) {
  // A run only counts for this process — switching processes leaves the previous
  // run's reference in the hook, and composing it here would save one process's
  // redesign into another's version.
  const rlRun = rl?.runRef?.processId === selectedId ? rl.runRef : null;
  const bprRun = bpr?.runRef?.processId === selectedId ? bpr.runRef : null;
  const rlAvailable = !!rlRun?.jobId;
  const bprAvailable = !!bprRun?.resultRef;

  const [selected, setSelected] = React.useState({ rl: false, bpr: false });
  const [payload, setPayload] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  // An `adopt` call that came back 400/500. Kept apart from `error` because the two
  // leave the tab in different states: `error` means there is no payload, while this
  // means the payload on screen is the PREVIOUS selection's and the ticks no longer
  // describe it. That second state must not blank the tab, and must not be saved.
  const [adoptError, setAdoptError] = React.useState("");
  const seqRef = React.useRef(0);

  const seenRef = React.useRef({ rl: false, bpr: false });

  // A different process means a different run, a different reference and a
  // different payload — none of the current state survives the switch. Declared
  // BEFORE the pre-check effect below so that on a process switch the clear runs
  // first and the pre-check gets the last word; the other order would wipe the
  // engine it had just ticked and never re-tick it.
  React.useEffect(() => {
    seqRef.current += 1;
    seenRef.current = { rl: false, bpr: false };
    setSelected({ rl: false, bpr: false });
    setPayload(null);
    setError("");
    setLoading(false);
  }, [selectedId]);

  // Pre-check an engine the analyst ran this session: they went to the trouble of
  // running it, so the default is to save it. Only ever auto-checks — unchecking
  // is a decision, and a later re-render must not silently undo it.
  React.useEffect(() => {
    setSelected((prev) => {
      let next = prev;
      if (rlAvailable && !seenRef.current.rl) next = { ...next, rl: true };
      if (bprAvailable && !seenRef.current.bpr) next = { ...next, bpr: true };
      return next;
    });
    seenRef.current = { rl: rlAvailable, bpr: bprAvailable };
  }, [rlAvailable, bprAvailable]);

  const useRl = rlAvailable && selected.rl;
  const useBpr = bprAvailable && selected.bpr;
  const composing = useRl || useBpr;

  // One request carries one include_subprocess_redesigns, and it has to be the flag
  // the engine tab ran with: if the reference has expired and the engine re-runs, a
  // mismatched flag produces a different redesign. When both engines are selected
  // and they disagree, RL's flag wins — RL is the stochastic one, so an unfaithful
  // re-run costs more there — and `subprocessFlagMismatch` says so on screen rather
  // than letting one of the two references quietly reproduce something else.
  const subprocessFlagMismatch =
    useRl && useBpr && !!rlRun.includeSubprocessRedesigns !== !!bprRun.includeSubprocessRedesigns;
  const includeSubprocessRedesigns = useRl
    ? !!rlRun.includeSubprocessRedesigns
    : useBpr
      ? !!bprRun.includeSubprocessRedesigns
      : false;

  // The exact query the payload was fetched with. The save POSTs the payload this
  // produced rather than re-fetching, so what the confirm step described is what
  // gets written.
  //
  // Depends on the reference VALUES, not on the run objects: a fresh RL run while
  // only BPR is selected changes rlRun's identity but not this query, and refetching
  // there would re-run the composition for no reason.
  const rlJobId = useRl ? rlRun.jobId : null;
  const bprResultRef = useBpr ? bprRun.resultRef : null;
  const engineParams = React.useMemo(() => {
    if (!rlJobId && !bprResultRef) return null;
    return {
      engines: ["agent", ...(rlJobId ? ["rl"] : []), ...(bprResultRef ? ["bpr"] : [])],
      rlJobId: rlJobId || undefined,
      bprResultRef: bprResultRef || undefined,
      includeSubprocessRedesigns,
    };
  }, [rlJobId, bprResultRef, includeSubprocessRedesigns]);

  // The analyst's heuristic selection, stamped with the engine query it was made
  // against. Stamping rather than clearing in an effect is what keeps the reset
  // SYNCHRONOUS with the engine change: ticking BPR off must not fire one request
  // carrying the old selection and then a second one without it, because the first
  // of those two is a real save-payload build and may re-run an engine.
  //
  // `keys: null` is not `keys: []`. Null is "first load — let the engines apply
  // their defaults" and omits the parameter; the empty array is the analyst saying
  // "write no engine structure at all", which is an instruction the backend has to
  // receive. See getOptimizedVersion for how the two reach the wire.
  const engineKey = React.useMemo(() => (engineParams ? JSON.stringify(engineParams) : ""), [engineParams]);
  const [adoptState, setAdoptState] = React.useState({ key: "", keys: null });
  const adoptKeys = adoptState.key === engineKey ? adoptState.keys : null;

  React.useEffect(() => {
    const seq = ++seqRef.current;
    if (!selectedId || !engineParams) {
      setPayload(null);
      setError("");
      setAdoptError("");
      setLoading(false);
      return;
    }
    const isAdoptCall = adoptKeys !== null;
    setLoading(true);
    setError("");
    setAdoptError("");
    getOptimizedVersion(selectedId, { ...engineParams, adopt: adoptKeys || undefined })
      .then((data) => {
        if (seq !== seqRef.current) return;
        setPayload(data);
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        const msg = e?.message || "Could not build the save payload for these engines.";
        // A rejected adopt call keeps the last good payload on screen. Blanking the
        // tab would throw away a composition that took an engine run to produce, and
        // the analyst can still recover by changing the ticks — but `saveBlocked`
        // below stops that stale payload from being written in the meantime.
        if (isAdoptCall) setAdoptError(msg);
        else {
          setPayload(null);
          setError(msg);
        }
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
  }, [selectedId, engineParams, adoptKeys]);

  const provenance = React.useMemo(() => readRedesignProvenance(payload), [payload]);
  const summary = React.useMemo(() => buildSaveSummary(provenance), [provenance]);

  const toggle = React.useCallback((engine) => {
    setSelected((prev) => ({ ...prev, [engine]: !prev[engine] }));
  }, []);

  // The checklist itself. On the first load the ticked set is whatever the engines
  // applied by default; after that it is exactly what the analyst last sent, so the
  // boxes keep showing their intent even when the response disagrees with it (a
  // heuristic that came back rejected_by_measurement stays ticked and says why).
  const heuristicRows = React.useMemo(() => readAppliedHeuristics(provenance), [provenance]);
  const warnings = React.useMemo(() => readSelectionWarnings(provenance), [provenance]);

  // Every row this engine query has ever shown, accumulated across the adopt calls.
  //
  // This exists because unticking a row can make it disappear from BOTH lists the
  // response carries: it leaves `applied_heuristics` by definition, and
  // `structural_heuristics` only holds the structural ones — so a non-persistable
  // row that is unticked has nowhere left to be rendered from. Without this the
  // analyst could untick a heuristic and have no way to put it back short of
  // resetting the whole selection. Keyed by engine query, so it is dropped the
  // moment the engines change and cannot resurrect a row from another composition.
  const [knownRows, setKnownRows] = React.useState({ key: "", rows: [] });
  React.useEffect(() => {
    if (!provenance) return;
    setKnownRows((prev) => {
      const seen = new Map(
        (prev.key === engineKey ? prev.rows : []).map((r) => [r.selection_key, r]),
      );
      for (const r of [...readAppliedHeuristics(provenance), ...readStructuralHeuristics(provenance)]) {
        if (r?.selection_key) seen.set(r.selection_key, r);
      }
      return { key: engineKey, rows: [...seen.values()] };
    });
  }, [provenance, engineKey]);

  const declinedRows = React.useMemo(() => {
    const fromBackend = readDeclinedHeuristics(provenance);
    const present = new Set(
      [...readAppliedHeuristics(provenance), ...fromBackend].map((r) => r?.selection_key).filter(Boolean),
    );
    // A row we have seen but the response no longer mentions was declined by the
    // analyst — restated locally with that status so it stays re-tickable.
    const vanished = (knownRows.key === engineKey ? knownRows.rows : [])
      .filter((r) => r.selection_key && !present.has(r.selection_key))
      .map((r) => ({ ...r, status: "not_selected" }));
    return [...fromBackend, ...vanished];
  }, [provenance, knownRows, engineKey]);

  const checkedKeys = React.useMemo(
    () => new Set(adoptKeys !== null ? adoptKeys : defaultAdoptKeys(provenance)),
    [adoptKeys, provenance],
  );

  const toggleHeuristic = React.useCallback(
    (row) => {
      const key = row?.selection_key;
      if (!key || !engineKey) return;
      setAdoptState((prev) => {
        const base = prev.key === engineKey && prev.keys !== null ? prev.keys : defaultAdoptKeys(provenance);
        const next = new Set(base);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return { key: engineKey, keys: [...next] };
      });
    },
    [engineKey, provenance],
  );

  /** Back to what the engines applied on their own — omits `adopt` entirely. */
  const resetHeuristicSelection = React.useCallback(() => {
    setAdoptState({ key: "", keys: null });
  }, []);

  /**
   * The ticked rows a version cannot carry as structure, in the shape the redesign
   * scenario writer takes. These are what makes a tick on an `applied_not_persistable`
   * row mean something: the workflow has no field for them, so they are linked to the
   * saved TO-BE scenario instead — the tick is honoured either way.
   *
   * DERIVED from the checkbox state rather than mirrored into a second store. The
   * two would otherwise start out of step on the very first render, where the
   * engines' defaults arrive pre-ticked without anyone having clicked anything, and
   * a row could read "ticked" on screen while nothing recorded it.
   */
  const adoptedFromChecklist = React.useMemo(() => {
    const out = {};
    for (const row of heuristicRows) {
      if (!row?.code || row.source === "agent" || carriesAsStructure(row)) continue;
      if (!row.selection_key || !checkedKeys.has(row.selection_key)) continue;
      out[row.code] = {
        code: row.code,
        name: row.name || row.code,
        rationale: row.rationale || row.measured_verdict || "",
        basis: row.basis || "",
        engineSource: row.source,
      };
    }
    return out;
  }, [heuristicRows, checkedKeys]);

  const selectionDirty = adoptKeys !== null;
  // Re-fetching with the previous payload still on screen. The checklist stays
  // mounted and greys out rather than unmounting: a list that vanishes on every
  // tick makes the analyst lose their place in it.
  const applying = loading && !!payload;

  // Two ways the payload can stop describing the ticks above it, both of which make
  // saving wrong rather than merely uncertain: the adopt call was refused, or the
  // engine moved underneath the selection (unmatched_keys) so the rows ticked are
  // not the rows in the response.
  const staleWarning = warnings.find((w) => w.key === "unmatched_keys") || null;
  const saveBlocked = !!adoptError || !!staleWarning;
  const saveBlockedReason = adoptError
    ? "Your last heuristic selection was refused, so the version below is still the previous one. Change the selection to try again."
    : staleWarning
      ? "The redesign changed after this list was loaded, so your selection no longer matches it. Reload the save tab before saving."
      : "";

  return {
    rlAvailable,
    bprAvailable,
    selected: { agent: true, rl: useRl, bpr: useBpr },
    toggle,
    composing,
    engineParams,
    includeSubprocessRedesigns,
    subprocessFlagMismatch,
    payload,
    loading,
    applying,
    error,
    adoptError,
    provenance,
    summary,
    heuristicRows,
    declinedRows,
    warnings,
    checkedKeys,
    adoptedFromChecklist,
    toggleHeuristic,
    resetHeuristicSelection,
    selectionDirty,
    saveBlocked,
    saveBlockedReason,
  };
}
